<template>
  <FsForm class="form" :model="form" :rules="rules" ref="formRef">
    <p class="export-project__title">What's included</p>
    <div class="checkbox_group">
      <FsCheckbox v-model="form.containTopologyPic" size="small">Topology</FsCheckbox>
      <FsCheckbox v-model="form.containEquipmentList" size="small">Equipment List</FsCheckbox>
    </div>

    <p class="export-project__divider"></p>
    <p class="export-project__title">Base Info</p>
    <FsFormItem label="Project Name" prop="name">
      <FsInput v-model="form.name" placeholder="Project Name" :maxlength="100" new-regular />
    </FsFormItem>
    <FsFormItem label="Project Area" prop="area">
      <FsSelect
        placeholder="Select project area"
        v-model="form.area"
        filterable
        search-placeholder="Search"
        :options="areaOptions"
        new-regular
      />
    </FsFormItem>
    <FsFormItem label="Project Description" prop="projectDescribe">
      <FsInput
        type="textarea"
        placeholder="Project Description"
        new-regular
        v-model="form.projectDescribe"
        :maxlength="1000"
      />
    </FsFormItem>
    <FsFormItem label="Create By" prop="createBy">
      <FsInput v-model="form.createBy" placeholder="Create By" new-regular :maxlength="30" />
    </FsFormItem>
    <FsFormItem label="Company Name" prop="corporateName">
      <FsInput v-model="form.corporateName" placeholder="Company Name" new-regular :maxlength="50" />
    </FsFormItem>
    <FsFormItem prop="corporateLogo" label="Company Logo">
      <FsUpload
        v-model="form.corporateLogo"
        @change="fileChange"
        text="Upload Company Logo"
        accept=".png, .jpeg, .jpg, .svg"
        new-regular
      ></FsUpload>
    </FsFormItem>
  </FsForm>

  <div class="footer_btn">
    <FsButton type="red" size="mediumSmall" @click="exportClick" :loading="loading">Export PDF</FsButton>
    <!-- <button type="button" class="cancel_btn" @click.stop="emits('close', form)">Cancel</button> -->
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, watch } from 'vue'
import { FsForm, FsFormItem, FsButton, FsInput, FsSelect, FsCheckbox, FsUpload, FsMessage } from 'fs-design'
import { uploadImage, getCountryList } from '@/api/modules/project'
import { useProductSelectionStore } from '@/store/productSelection'
import { trackEvent } from '@/utils/analytics'
import { useFormCache } from '@/hooks/useFormCache'
import axios from 'axios'
import { Cookie, parseUrlParams } from '@/utils'
const productSelectionStore = useProductSelectionStore()

interface Props {
  data: Record<string, unknown>
}
interface Option {
  label: string
  value: string
}

interface UploadFile {
  files: File[]
}

const formRef = ref<InstanceType<typeof FsForm> | null>(null)

const emits = defineEmits<{
  close: [formData?: typeof form.value]
}>()
const props = defineProps<Props>()

const form = ref({
  containTopologyPic: true,
  containEquipmentList: true,
  name: '',
  area: '',
  createBy: '',
  corporateName: '',
  corporateLogo: '',
  // corporateLogoUrl: '', // 保存上传后的 URL
  projectDescribe: '',
})

const rules = ref({
  name: [{ required: true, message: 'Please input project name' }],
})

const areaOptions = ref<Option[]>([])
const loading = ref(false)

const fileChange = (val: UploadFile) => {
  if (val.files.length) {
    const params = new FormData()
    val.files.forEach((item: any) => {
      params.append('file', item)
    })
    uploadImage(params).then((res: any) => {
      form.value.corporateLogo = res.data
    })
  } else {
    form.value.corporateLogo = ''
  }
}

const exportClick = async () => {
  trackEvent({
    eventCategory: 'dec_topology',
    eventAction: 'pdf',
    eventLabel: 'export_pdf',
  })
  try {
    await formRef.value.validate()

    if (loading.value) {
      return
    }

    loading.value = true

    try {
      // 获取 token
      const urlParams = parseUrlParams()
      const token =
        Cookie.get('token_new') ||
        urlParams.token ||
        location.search.split('token=')[1] ||
        localStorage.getItem('token_new') ||
        ''

      // 使用原生 axios 发送请求
      const response = await axios.post(
        `${import.meta.env.VITE_API_BASE_URL}/design/export/customer/pdf`,
        {
          projectId: props.data.id,
          logoUrl: form.value.corporateLogo,
          companyName: form.value.corporateName,
          projectName: form.value.name,
          projectDescribe: form.value.projectDescribe,
          createUerName: form.value.createBy,
          containTopologyPic: form.value.containTopologyPic,
          containEquipmentList: form.value.containEquipmentList,
          projectLocation: form.value.area,
          productIdList: productSelectionStore.selectedProductIds,
        },
        {
          responseType: 'blob',
          headers: {
            'Content-Type': 'application/json',
            ...(token && { authorization: token }),
          },
        }
      )

      console.log(response, '客户版导出PDF-----🚀')

      trackEvent({
        eventCategory: 'dec_topology',
        eventAction: 'pdf',
        eventLabel: 'pdf_generated',
      })

      // 从响应头获取文件名
      const contentDisposition = response.headers['content-disposition']
      const match = contentDisposition?.match(/filename="?(.+?)"?$/)
      const filename = match ? decodeURIComponent(match[1]) : 'customer-project.pdf'

      // 创建 Blob 对象并下载
      const blob = new Blob([response.data], { type: 'application/pdf' })
      const blobUrl = URL.createObjectURL(blob)

      const link = document.createElement('a')
      link.href = blobUrl
      link.download = filename
      document.body.appendChild(link)
      link.click()

      // 清理资源
      URL.revokeObjectURL(blobUrl)
      link.remove()

      loading.value = false
      emits('close', form.value)
    } catch (err) {
      console.error('PDF 导出失败:', err)
      trackEvent({
        eventCategory: 'dec_topology',
        eventAction: 'error',
        eventLabel: String((err as any)?.message || err),
      })
      FsMessage({
        type: 'danger',
        message: '导出失败，请稍后重试',
      })
      loading.value = false
    }
  } catch (e) {
    console.log(e)
  }
}
const getCountry = () => {
  getCountryList().then((res: any) => {
    const list = Array.isArray(res?.data) ? res.data : []
    areaOptions.value = list
      .map((item: any) => ({ label: item.countryEn, value: item.countryEn }))
      .filter(opt => opt.label && opt.value)
  })
}

onMounted(() => {
  getCountry()
})

watch(
  () => props.data,
  newVal => {
    // 基础项目信息
    console.log(newVal, 'newVal---')
    if (newVal.name) {
      form.value.name = newVal.name as string
    }

    // 如果缓存数据中有这些字段，优先使用缓存数据
    if (newVal.containTopologyPic !== undefined) {
      form.value.containTopologyPic = newVal.containTopologyPic as boolean
    }
    if (newVal.containEquipmentList !== undefined) {
      form.value.containEquipmentList = newVal.containEquipmentList as boolean
    }
    if (newVal.area) {
      form.value.area = newVal.area as string
    }
    if (newVal.createBy !== undefined) {
      form.value.createBy = newVal.createBy as string
    }
    if (newVal.createdBy !== undefined && !newVal.createBy) {
      form.value.createBy = newVal.createdBy as string
    }
    if (newVal.corporateName !== undefined) {
      form.value.corporateName = newVal.corporateName as string
    }
    // if (newVal.corporateLogo !== undefined) {
    //   // corporateLogo 可能是字符串 URL，也可能是缓存的数组格式
    //   let logoUrl = ''

    //   // 判断是字符串还是数组
    //   if (typeof newVal.corporateLogo === 'string') {
    //     logoUrl = newVal.corporateLogo
    //     form.value.corporateLogoUrl = logoUrl

    //     // 如果有 URL 字符串，创建用于回显的对象
    //     if (logoUrl) {
    //       console.log(logoUrl, 'logoUrl')
    //       // 从 URL 获取文件名
    //       const fileName = logoUrl.split('/').pop() || 'logo.svg'

    //       // FsUpload 组件需要的文件对象格式
    //       const fileObj = {
    //         url: logoUrl,
    //         name: fileName,
    //         status: 'done',
    //         uid: Date.now().toString(),
    //         size: 1024, // 添加一个非零的 size
    //         type: 'image/svg+xml',
    //       }

    //       form.value.corporateLogo = [fileObj]
    //       console.log(form.value.corporateLogo, 'form.value.corporateLogo')
    //     } else {
    //       form.value.corporateLogo = []
    //     }
    //     console.log(form.value.corporateLogo, 'corporateLogo (from URL)')
    //   } else if (Array.isArray(newVal.corporateLogo)) {
    //     // 如果是数组格式（来自缓存），直接使用
    //     form.value.corporateLogo = newVal.corporateLogo as any[]
    //     // 尝试从数组中提取 URL
    //     if (newVal.corporateLogo.length > 0) {
    //       const file = newVal.corporateLogo[0]
    //       logoUrl = (file as any).url || form.value.corporateLogoUrl
    //     }
    //     form.value.corporateLogoUrl = logoUrl
    //     console.log(form.value.corporateLogo, 'corporateLogo (from cache)')
    //   }
    // }
    if (newVal.projectDescribe !== undefined) {
      form.value.projectDescribe = newVal.projectDescribe as string
    } else if (newVal.description) {
      form.value.projectDescribe = newVal.description as string
    } else if (newVal.templateDescription) {
      form.value.projectDescribe = newVal.templateDescription as string
    }
  },
  { immediate: true, deep: true }
)

// 暴露方法给父组件
defineExpose({
  getFormData: () => form.value,
})
</script>

<style lang="scss" scoped>
.form {
  padding: 16px 20px 0px 20px;
  flex: 1 1 auto;
  overflow: auto;

  :deep(.fs-upload) {
    .fs-upload__wrap {
      display: block;
      .fs-upload__box {
        .fs-button--gray.is-plain {
          background: transparent;
          color: #19191a;
        }
      }
    }
  }
}

.footer_btn {
  padding: 20px 20px 24px 20px;
  flex-shrink: 0;
  display: flex;
  flex-direction: row-reverse;

  .cancel_btn {
    @include textButton();
    margin-right: 16px;
  }
}
.fs-upload :deep(.fs-upload-files) {
  // margin-top: 28px;
}
</style>
